import { Modal, Box, Text, Stack, ScrollArea } from '@mantine/core';
import { RISK_DISPLAY_CONFIG } from '../../constants/riskDisplay';
import { FIELD_EVENT_ICONS } from '../../constants/fieldState';
import { colors } from '../../theme/colors';
import { RiskIconBox } from './RiskIconBox';
import { MaterialIcon } from './MaterialIcon';

interface LegendModalProps {
  opened: boolean;
  onClose: () => void;
}

/** 凡例の1行（アイコン + ラベル）の共通レイアウト */
const LegendRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>{children}</Box>
);

/** セクションタイトル */
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text fw={700} size="sm">
    {children}
  </Text>
);

/**
 * 凡例モーダルコンポーネント
 * カレンダー上の各ビジュアル要素の意味を説明する
 */
export const LegendModal: React.FC<LegendModalProps> = ({ opened, onClose }) => {
  // リスク種類の表示順（RISK_TYPES の値に揃える）
  const riskTypes = ['crust', 'frost', 'wind', 'heavy_rain', 'div'] as const;

  return (
    <Modal opened={opened} onClose={onClose} title="凡例" centered size="md" withCloseButton>
      <ScrollArea.Autosize mah="70vh">
        <Stack gap="xl">
          {/* セクション1: 作業可否（横並び） */}
          <Box>
            <SectionTitle>作業可否</SectionTitle>
            <Box style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '8px' }}>
              <LegendRow>
                <Box
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: '#ffffff',
                    border: `1px solid ${colors.gray[300]}`,
                    borderRadius: '4px',
                  }}
                />
                <Text size="sm">可能</Text>
              </LegendRow>
              <LegendRow>
                <Box
                  style={{
                    width: '24px',
                    height: '24px',
                    backgroundColor: colors.workAvailability.unavailable,
                    border: `1px solid ${colors.gray[300]}`,
                    borderRadius: '4px',
                  }}
                />
                <Text size="sm">不可</Text>
              </LegendRow>
            </Box>
          </Box>

          {/* セクション2: 圃場イベント（Figma準拠） */}
          <Box>
            <SectionTitle>圃場イベント</SectionTitle>
            <Stack gap="md" mt="xs">
              {/* 播種候補日 + 推奨度シンボル */}
              <Box>
                <LegendRow>
                  {/* カレンダー上と同じ ○/A の縦並び表示 */}
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '24px',
                    }}
                  >
                    <span
                      style={{ fontSize: '20px', color: colors.semantic.warning, lineHeight: '1' }}
                    >
                      ○
                    </span>
                    <span
                      style={{ fontSize: '12px', color: colors.semantic.warning, lineHeight: '1' }}
                    >
                      A
                    </span>
                  </Box>
                  <Text size="sm" fw={500}>
                    播種候補日
                  </Text>
                  <Text size="xs" c="dimmed">
                    {/* eslint-disable-next-line no-irregular-whitespace */}
                    ○:好条件　△:やや好条件　×:悪条件
                  </Text>
                </LegendRow>
                {/* 播種予定日 / 播種実施日 */}
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '4px' }}
                >
                  <LegendRow>
                    {/* カレンダー上は ○/A ブロックに破線アンダーラインで表現されるため、
                        凡例でもアイコン単体 + 破線アンダーラインで示す */}
                    <Box
                      style={{
                        display: 'inline-flex',
                        borderBottom: `2px dotted ${colors.semantic.warning}`,
                        paddingBottom: '1px',
                      }}
                    >
                      <MaterialIcon
                        icon={FIELD_EVENT_ICONS.SEEDING}
                        size="sm"
                        color={colors.semantic.warning}
                      />
                    </Box>
                    <Text size="sm">播種予定日</Text>
                  </LegendRow>
                  <LegendRow>
                    <MaterialIcon
                      icon={FIELD_EVENT_ICONS.SEEDING}
                      size="sm"
                      color={colors.semantic.warning}
                      circled="solid"
                    />
                    <Text size="sm">播種実施日</Text>
                  </LegendRow>
                </Box>
              </Box>

              {/* (播種候補日に対する)予測出芽日 */}
              <Box>
                <LegendRow>
                  {/* カレンダー上と同じ 🌱/A の縦並び表示 */}
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '24px',
                    }}
                  >
                    <MaterialIcon
                      icon={FIELD_EVENT_ICONS.EMERGENCE}
                      size="sm"
                      color={colors.semantic.success}
                    />
                    <span
                      style={{ fontSize: '12px', color: colors.semantic.success, lineHeight: '1' }}
                    >
                      A
                    </span>
                  </Box>
                  <Text size="sm" fw={500}>
                    (播種候補日に対する)予測出芽日
                  </Text>
                </LegendRow>
                {/* 予測出芽日 / 出芽日 */}
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '4px' }}
                >
                  <LegendRow>
                    <MaterialIcon
                      icon={FIELD_EVENT_ICONS.EMERGENCE}
                      size="sm"
                      color={colors.semantic.success}
                      circled="dashed"
                    />
                    <Text size="sm">予測出芽日</Text>
                  </LegendRow>
                  <LegendRow>
                    <MaterialIcon
                      icon={FIELD_EVENT_ICONS.EMERGENCE}
                      size="sm"
                      color={colors.semantic.success}
                      circled="solid"
                    />
                    <Text size="sm">出芽日</Text>
                  </LegendRow>
                </Box>
              </Box>

              {/* 防除候補日 + 推奨度シンボル */}
              <Box>
                <LegendRow>
                  {/* カレンダー上と同じ ○/A の縦並び表示 */}
                  <Box
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      width: '24px',
                    }}
                  >
                    <span
                      style={{ fontSize: '20px', color: colors.semantic.success, lineHeight: '1' }}
                    >
                      ○
                    </span>
                    <span
                      style={{ fontSize: '12px', color: colors.semantic.success, lineHeight: '1' }}
                    >
                      A
                    </span>
                  </Box>
                  <Text size="sm" fw={500}>
                    防除候補日
                  </Text>
                  <Text size="xs" c="dimmed">
                    {/* eslint-disable-next-line no-irregular-whitespace */}
                    ○:好条件　△:やや好条件　×:悪条件
                  </Text>
                </LegendRow>
                {/* 防除予定日 / 防除実施日 */}
                <Box
                  style={{ display: 'flex', alignItems: 'center', gap: '16px', marginTop: '4px' }}
                >
                  <LegendRow>
                    {/* カレンダー上は防除推奨 ○/A ブロックに破線アンダーラインで表現されるため、
                        凡例でもアイコン単体 + 破線アンダーラインで示す */}
                    <Box
                      style={{
                        display: 'inline-flex',
                        borderBottom: `2px dotted ${colors.semantic.success}`,
                        paddingBottom: '1px',
                      }}
                    >
                      <MaterialIcon
                        icon={FIELD_EVENT_ICONS.PEST_CONTROL}
                        size="sm"
                        color={colors.semantic.success}
                      />
                    </Box>
                    <Text size="sm">防除予定日</Text>
                  </LegendRow>
                  <LegendRow>
                    <MaterialIcon
                      icon={FIELD_EVENT_ICONS.PEST_CONTROL}
                      size="sm"
                      color={colors.semantic.success}
                      circled="solid"
                    />
                    <Text size="sm">防除実施日</Text>
                  </LegendRow>
                </Box>
              </Box>
            </Stack>
          </Box>

          {/* セクション3: 気象災害リスク（テーブル形式） */}
          <Box>
            <SectionTitle>気象災害リスク</SectionTitle>
            <table
              style={{
                marginTop: '8px',
                borderCollapse: 'collapse',
                width: '100%',
              }}
            >
              <thead>
                <tr style={{ borderBottom: `1px solid ${colors.gray[300]}` }}>
                  <th style={{ textAlign: 'center', padding: '4px 0' }}>
                    <Text size="xs" c="dimmed">
                      リスク種類
                    </Text>
                  </th>
                  <th style={{ textAlign: 'center', padding: '4px 0' }}>
                    <Text size="xs" c="dimmed">
                      高リスク
                    </Text>
                  </th>
                  <th style={{ textAlign: 'center', padding: '4px 0' }}>
                    <Text size="xs" c="dimmed">
                      低リスク
                    </Text>
                  </th>
                </tr>
              </thead>
              <tbody>
                {riskTypes.map((riskType) => {
                  const config = RISK_DISPLAY_CONFIG[riskType];
                  if (!config) return null;
                  return (
                    <tr key={riskType} style={{ borderBottom: `1px solid ${colors.gray[200]}` }}>
                      <td style={{ padding: '6px 0', textAlign: 'center' }}>
                        <Text size="sm">{config.label}</Text>
                      </td>
                      <td style={{ padding: '6px 0', textAlign: 'center' }}>
                        <Box style={{ display: 'inline-flex' }}>
                          <RiskIconBox riskType={riskType} grade="warning" size="sm" />
                        </Box>
                      </td>
                      <td style={{ padding: '6px 0', textAlign: 'center' }}>
                        <Box style={{ display: 'inline-flex' }}>
                          <RiskIconBox riskType={riskType} grade="caution" size="sm" />
                        </Box>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Stack>
      </ScrollArea.Autosize>
    </Modal>
  );
};
